import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { db } from '../firebase';
import { collection, onSnapshot, doc, DocumentData } from 'firebase/firestore';
import { useAuth } from './AuthContext';
import { useQueryClient } from '@tanstack/react-query';
import { Store, StoreContextType } from '../types';

const StoreContext = createContext<StoreContextType | undefined>(undefined);

export function useStore(): StoreContextType {
    const context = useContext(StoreContext);
    if (context === undefined) {
        throw new Error('useStore must be used within a StoreProvider');
    }
    return context;
}

interface StoreProviderProps {
    children: ReactNode;
}

export function StoreProvider({ children }: StoreProviderProps): JSX.Element {
    const [stores, setStores] = useState<Store[]>([]);
    const [currentStore, setCurrentStore] = useState<Store | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const { currentUser } = useAuth();
    const queryClient = useQueryClient();

    // Invalidate all store-scoped queries when store changes
    const invalidateStoreQueries = useCallback((): void => {
        // Invalidate all queries that depend on store data
        queryClient.invalidateQueries({ queryKey: ['inventory'] });
        queryClient.invalidateQueries({ queryKey: ['sales'] });
        queryClient.invalidateQueries({ queryKey: ['customers'] });
        queryClient.invalidateQueries({ queryKey: ['expenses'] });
        queryClient.invalidateQueries({ queryKey: ['repairs'] });
        queryClient.invalidateQueries({ queryKey: ['salesProducts'] });
        queryClient.invalidateQueries({ queryKey: ['users'] });
    }, [queryClient]);

    // Track previous store ID for change detection
    const [prevStoreId, setPrevStoreId] = useState<string | null>(null);

    // Custom setter that also invalidates cache
    const handleStoreChange = useCallback((newStore: Store | null): void => {
        setCurrentStore(newStore);
    }, []);

    // Watch for store changes and invalidate cache
    useEffect(() => {
        if (currentStore?.id !== prevStoreId) {
            // Store actually changed - invalidate cache
            if (prevStoreId !== null) {
                // Don't invalidate on initial load (when prevStoreId is null)
                invalidateStoreQueries();
            }
            setPrevStoreId(currentStore?.id || null);
        }
    }, [currentStore, prevStoreId, invalidateStoreQueries]);

    useEffect(() => {
        let unsubscribeStores = (): void => { };
        let unsubscribeUser = (): void => { };

        function setupListeners(): void {
            setLoading(true);

            // 1. Listen to all stores
            unsubscribeStores = onSnapshot(collection(db, "stores"), (snapshot) => {
                const allStores: Store[] = snapshot.docs.map(d => ({
                    id: d.id,
                    ...d.data()
                } as Store));

                // If we have a user, we need to filter these stores
                if (currentUser) {
                    setupUserListener(allStores);
                } else {
                    setStores(allStores);
                    setLoading(false);
                }
            }, () => {
                setLoading(false);
            });
        }

        function setupUserListener(allStores: Store[]): void {
            if (!currentUser) return;
            const userId = currentUser.uid || currentUser.id;
            if (!userId) return;

            // 2. Listen to current user's document for role/assignments changes
            unsubscribeUser = onSnapshot(doc(db, "users", userId), (userDoc) => {
                if (userDoc.exists()) {
                    const userData = userDoc.data() as DocumentData;
                    let accessibleStores: Store[] = [];

                    if (userData.role === 'admin') {
                        accessibleStores = allStores;
                    } else {
                        const assignedIds = (userData.assignedStoreIds as string[]) || [];
                        accessibleStores = allStores.filter(store => assignedIds.includes(store.id));
                    }

                    setStores(accessibleStores);

                    // Logic to set/reset current store
                    setCurrentStore(prevStore => {
                        // If no store selected, or current store is no longer accessible
                        if (!prevStore || !accessibleStores.find(s => s.id === prevStore.id)) {
                            // Prefer the default store, otherwise use first available
                            const defaultStore = accessibleStores.find(s => s.isDefault);
                            return defaultStore || (accessibleStores.length > 0 ? accessibleStores[0] : null);
                        }
                        return prevStore;
                    });
                } else {
                    setStores([]);
                }
                setLoading(false);
            }, () => {
                setLoading(false);
            });
        }

        if (currentUser) {
            setupListeners();
        } else {
            setStores([]);
            setCurrentStore(null);
            setLoading(false);
        }

        return () => {
            unsubscribeStores();
            unsubscribeUser();
        };
    }, [currentUser]); // Re-run if user changes (login/logout)

    const value: StoreContextType = {
        stores,
        currentStore,
        setCurrentStore: handleStoreChange, // Use the wrapped setter that invalidates cache
        invalidateStoreQueries, // Expose for manual invalidation if needed
        loading
    };

    return (
        <StoreContext.Provider value={value}>
            {!loading && children}
        </StoreContext.Provider>
    );
}
